import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { ccApi, type CcDraft, type CcMode, type CcPolicyView } from './ccApi';

type Field = 'enabled' | 'mode' | 'paths';
const pendingStates = new Set(['QUEUED', 'PREPARING', 'PUBLISHING', 'UNKNOWN', 'AWAITING_ACK', 'FAILED_PENDING', 'TIMED_OUT', 'RETRYING', 'NO_TARGETS']);
const names: Record<string, string> = {
  NOT_PUBLISHED: '尚未发布', QUEUED: '已保存，等待发布', PREPARING: '正在生成配置',
  PUBLISHING: '正在发布', AWAITING_ACK: '等待节点确认', APPLIED: '全部目标节点已生效',
  FAILED: '发布失败', FAILED_PENDING: '部分节点失败，仍在等待其他节点',
  UNKNOWN: '暂时无法判断发布结果', TIMED_OUT: '节点确认超时', RETRYING: '正在重试生成配置',
  NO_TARGETS: '没有可确认的发布节点', SUPERSEDED: '已被后续修改替代'
};

export function ccErrorMessage(error: unknown, fallback: string) {
  const value = error as { message?: string; status?: number; code?: string } | null;
  if (value?.status === 404 || value?.code === 'METHOD_NOT_ALLOWED') return '当前后端尚未开放 CC 配置接口，不能保存或宣称已生效。';
  const message = value?.message?.trim();
  return !message || message.includes('系统繁忙') ? fallback : message;
}

/** One domain-local edit queue. A save response updates the acknowledged version, never the newer draft. */
export function useCcPolicy(domainId: number) {
  const view = ref<CcPolicyView | null>(null);
  const draft = ref<CcDraft>({ enabled: true, mode: 'STANDARD', pathExceptions: [] });
  const loading = ref(true);
  const saving = ref(false);
  const loadError = ref('');
  const saveError = ref('');
  const publishError = ref('');
  const fieldNotes = ref<Partial<Record<Field, string>>>({});
  const pathInput = ref('');
  const pathMatch = ref<'EXACT' | 'PREFIX'>('EXACT');
  const pathError = ref('');
  const version = ref(0);
  const acknowledged = ref('');
  const remoteConflict = ref(false);
  const modes: CcMode[] = ['RELAXED', 'STANDARD', 'STRICT'];
  const editable = computed(() => !loading.value && !loadError.value && !remoteConflict.value
    && (!view.value?.policy || modes.includes(view.value.policy.mode as CcMode)));
  const dirty = computed(() => !!acknowledged.value && signature(draft.value) !== acknowledged.value);
  const statusText = computed(() => saving.value ? '正在保存配置' : dirty.value ? '有修改待保存'
    : names[view.value?.publishState || 'NOT_PUBLISHED'] || '无法判断发布状态');
  const durationText = computed(() => {
    const publication = view.value?.publication;
    if (view.value?.publishState !== 'APPLIED' || !publication?.dispatchedAt || !publication.completedAt) return '';
    const seconds = (Date.parse(publication.completedAt) - Date.parse(publication.dispatchedAt)) / 1000;
    return Number.isFinite(seconds) && seconds >= 0 ? `本次从开始发布到全部节点确认：${Math.ceil(seconds)} 秒` : '';
  });
  let disposed = false;
  let serial = 0;
  let operation = 0;
  let pathSerial = 0;
  let saveDeadline = 0;
  let blocked = false;
  let pollFailures = 0;
  let saveTimer: ReturnType<typeof setTimeout> | undefined;
  let pollTimer: ReturnType<typeof setTimeout> | undefined;
  const noteTimers = new Map<Field, ReturnType<typeof setTimeout>>();
  const changed = new Map<Field, number>();

  function signature(value: CcDraft) { return JSON.stringify(value); }
  function clone(): CcDraft { return JSON.parse(signature(draft.value)) as CcDraft; }
  function note(field: Field, message: string) {
    if (disposed) return;
    clearTimeout(noteTimers.get(field));
    fieldNotes.value = { ...fieldNotes.value, [field]: message };
    noteTimers.set(field, setTimeout(() => {
      const next = { ...fieldNotes.value }; delete next[field]; fieldNotes.value = next;
      noteTimers.delete(field);
    }, 15_000));
  }

  function edited(field: Field) {
    serial++; changed.set(field, serial); blocked = false; saveError.value = '';
    note(field, '修改后 1 秒自动保存，不影响其他操作');
    saveDeadline = Date.now() + 1000;
    scheduleSave();
  }

  function scheduleSave() {
    clearTimeout(saveTimer);
    if (disposed) { void save(); return; }
    saveTimer = setTimeout(() => void save(), Math.max(0, saveDeadline - Date.now()));
  }

  function setMode(mode: CcMode) {
    if (!editable.value || draft.value.mode === mode) return;
    draft.value.mode = mode; edited('mode');
  }
  function setEnabled(enabled: boolean) {
    if (!editable.value || draft.value.enabled === enabled) return;
    draft.value.enabled = enabled; edited('enabled');
  }

  function addPath() {
    if (!editable.value) return;
    const path = pathInput.value.trim();
    if (!path) { pathError.value = ''; return; }
    if (path.length > 1024 || !path.startsWith('/') || /[%?#\\\s]/.test(path) || path.includes('//')
        || path.split('/').some(part => part === '.' || part === '..')) {
      pathError.value = '请输入以 / 开头的路径，不要包含空白、查询参数、编码分隔符或相对目录。'; return;
    }
    if (pathMatch.value === 'PREFIX' && path === '/') { pathError.value = '不能将整个站点设置为路径豁免。'; return; }
    if (draft.value.pathExceptions.length >= 128) { pathError.value = '最多添加 128 条路径豁免。'; return; }
    if (draft.value.pathExceptions.some(item => item.path === path && item.matchType === pathMatch.value)) {
      pathError.value = '这条路径已经添加，不会重复保存。'; return;
    }
    draft.value.pathExceptions.push({ id: `path-${Date.now().toString(36)}-${++pathSerial}`, matchType: pathMatch.value, path });
    pathInput.value = ''; pathError.value = ''; edited('paths');
  }

  function pathFocusOut(event: FocusEvent) {
    const container = event.currentTarget as HTMLElement;
    if (event.relatedTarget instanceof Node && container.contains(event.relatedTarget)) return;
    addPath(); // Empty blur is a no-op; deletion is bound only to the explicit remove button.
  }

  function removePath(id: string) {
    if (!editable.value) return;
    draft.value.pathExceptions = draft.value.pathExceptions.filter(item => item.id !== id);
    edited('paths');
  }

  async function load() {
    clearTimeout(pollTimer); clearTimeout(saveTimer);
    const ticket = ++operation;
    loading.value = true; loadError.value = ''; saveError.value = ''; publishError.value = '';
    try {
      const response = await ccApi.get(domainId);
      if (disposed || ticket !== operation) return;
      if (response.domainId !== domainId || !Number.isSafeInteger(response.policyVersion)) throw new Error('CC 配置返回的域名或版本不一致。');
      view.value = response; version.value = response.policyVersion;
      draft.value = { enabled: response.policy?.enabled ?? true, mode: (response.policy?.mode ?? 'STANDARD') as CcMode,
        pathExceptions: response.policy?.pathExceptions?.map(item => ({ ...item })) ?? [] };
      acknowledged.value = signature(draft.value); remoteConflict.value = false; blocked = false;
      changed.clear(); fieldNotes.value = {}; pathInput.value = ''; pathError.value = '';
      schedulePoll();
    } catch (error) {
      if (!disposed && ticket === operation) loadError.value = ccErrorMessage(error, '无法判断 CC 配置加载失败原因，请重试。');
    } finally { if (!disposed && ticket === operation) loading.value = false; }
  }

  async function reload() {
    if (saving.value) return;
    if ((dirty.value || pathInput.value.trim()) && !window.confirm('读取最新配置会放弃此处尚未保存的修改，确定继续？')) return;
    await load();
  }

  async function save() {
    if (saving.value || blocked || !editable.value || !dirty.value) return;
    clearTimeout(saveTimer);
    const sent = clone(); const sentSignature = signature(sent); const sentSerial = serial;
    ++operation; saving.value = true; saveError.value = ''; clearTimeout(pollTimer);
    try {
      const response = await ccApi.save(domainId, sent, version.value);
      if (response.domainId !== domainId || !Number.isSafeInteger(response.policyVersion) || response.policyVersion < version.value) {
        throw new Error('CC 保存返回的域名或版本不一致，不能判断保存成功。');
      }
      // Keep this acknowledged version even after unmount, so an already queued later edit can drain safely.
      version.value = response.policyVersion; acknowledged.value = sentSignature; view.value = response;
      for (const [field, fieldSerial] of changed) {
        if (fieldSerial <= sentSerial) {
          changed.delete(field);
          note(field, response.dispatchEnabled ? '已保存，节点发布状态见下方' : '已保存，等待后台启用节点发布');
        }
      }
      schedulePoll();
    } catch (error) {
      blocked = true;
      saveError.value = ccErrorMessage(error, '无法判断是否保存成功，输入已保留，请读取最新配置核对后重试。');
    } finally {
      saving.value = false;
      if (!blocked && dirty.value) scheduleSave();
    }
  }

  function retrySave() { blocked = false; saveDeadline = 0; void save(); }

  function schedulePoll() {
    clearTimeout(pollTimer);
    if (disposed || !view.value?.dispatchEnabled || !pendingStates.has(view.value.publishState)) return;
    pollTimer = setTimeout(() => void poll(), Math.min(30_000, 2000 * 2 ** pollFailures));
  }

  async function poll() {
    if (disposed || saving.value) { schedulePoll(); return; }
    const ticket = operation;
    try {
      const response = await ccApi.get(domainId);
      if (disposed || ticket !== operation || saving.value) return;
      if (response.domainId !== domainId || response.policyVersion < version.value) throw new Error('节点状态返回了不一致的配置版本。');
      if (response.policyVersion > version.value) {
        remoteConflict.value = true; blocked = true;
        publishError.value = '策略已被其他操作更新。当前输入已保留，请读取最新配置后继续。'; return;
      }
      view.value = response; publishError.value = ''; pollFailures = 0;
      // Polling changes publication status only. It must never replace fields while the user is typing.
    } catch (error) {
      if (!disposed && ticket === operation) {
        pollFailures = Math.min(4, pollFailures + 1);
        publishError.value = ccErrorMessage(error, '无法判断当前节点生效状态，将继续核查。');
      }
    } finally { if (!disposed && ticket === operation && !remoteConflict.value) schedulePoll(); }
  }

  onMounted(load);
  onBeforeUnmount(() => {
    disposed = true; clearTimeout(saveTimer); clearTimeout(pollTimer);
    for (const timer of noteTimers.values()) clearTimeout(timer);
    // SPA navigation does not discard an already confirmed edit waiting in the one-second queue.
    saveDeadline = 0;
    if (!saving.value && !blocked && dirty.value) void save();
  });

  return { view, draft, loading, saving, loadError, saveError, publishError, fieldNotes, editable, dirty,
    statusText, durationText, pathInput, pathMatch, pathError, remoteConflict,
    setMode, setEnabled, addPath, removePath, pathFocusOut, reload, retrySave };
}
